import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { Router, NavigationEnd, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs/operators';

export interface HeaderConfig {
    showBackButton: boolean;
    showSearch: boolean;
    showActivities: boolean;
    showQuickActions: boolean;
    pageTitle?: string;
}

@Injectable({
    providedIn: 'root'
})
export class HeaderService {
    private readonly headerConfigSubject = new BehaviorSubject<HeaderConfig>({
        showBackButton: false,
        showSearch: true,
        showActivities: true,
        showQuickActions: true,
        pageTitle: ''
    });

    public headerConfig$: Observable<HeaderConfig> = this.headerConfigSubject.asObservable();

    constructor(
        private readonly router: Router,
        private readonly location: Location,
        private readonly activatedRoute: ActivatedRoute
    ) {
        // Automatically configure header based on route data
        this.initRouteListener();
    }

    /**
     * Listen to route changes and configure header based on route data
     */
    private initRouteListener() {
        this.router.events
            .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
            .subscribe((event) => {
                const routeData = this.getRouteData(this.activatedRoute);
                
                if (routeData?.['header']) {
                    const headerConfig = routeData['header'];
                    
                    if (headerConfig.type === 'detail') {
                        this.showDetailPageHeader(headerConfig.title || '');
                    } else if (headerConfig.type === 'dashboard') {
                        this.showDashboardHeader();
                    } else {
                        // Custom configuration
                        this.updateHeaderConfig(headerConfig);
                    }
                }

                const url = event.urlAfterRedirects || event.url;
                if (url.startsWith('/contract-master')) {
                    this.updateHeaderConfig({ showActivities: true });
                }
            });
    }

    /**
     * Get route data from the deepest child route
     */
    private getRouteData(route: ActivatedRoute): any {
        let child = route.firstChild;
        while (child) {
            if (child.firstChild) {
                child = child.firstChild;
            } else if (child.snapshot.data) {
                return child.snapshot.data;
            } else {
                return null;
            }
        }
        return route.snapshot.data;
    }

    /**
     * Update header configuration
     */
    updateHeaderConfig(config: Partial<HeaderConfig>) {
        const currentConfig = this.headerConfigSubject.value;
        this.headerConfigSubject.next({ ...currentConfig, ...config });
    }

    /**
     * Show detail page header (with back button, no search)
     */
    showDetailPageHeader(pageTitle: string) {
        this.updateHeaderConfig({
            showBackButton: true,
            showSearch: false,
            showActivities: false,
            showQuickActions: false,
            pageTitle
        });
    }

    /**
     * Show normal dashboard header (with search, no back button)
     */
    showDashboardHeader() {
        this.updateHeaderConfig({
            showBackButton: false,
            showSearch: true,
            showActivities: true,
            showQuickActions: true,
            pageTitle: ''
        });
    }

    /**
     * Navigate back
     */
    goBack() {
        console.log("INSIDE GO BACK")
        this.location.back();
    }

    /**
     * Get current header config
     */
    getCurrentConfig(): HeaderConfig {
        return this.headerConfigSubject.value;
    }
}

